/**
 * The API contract, mirroring server/src/types/domain.ts.
 *
 * Kept as a hand-written copy rather than a shared package: the server and
 * web app build independently, and the contract is small enough that a
 * generated client would cost more than it saves. Update both together.
 */

export const TRAVEL_MODES = ['walk', 'bike', 'motorcycle', 'car', 'taxi', 'truck', 'transit'] as const;
export type TravelMode = (typeof TRAVEL_MODES)[number];

/**
 * The modes a picker offers, which is not every mode there is.
 *
 * `transit` is left out. It needs a transit graph built from GTFS feeds by
 * OpenTripPlanner, and `otp_build` is refused on every deployment — it is in
 * `unbuiltTypes`, not merely absent from one mode. So the button was there on
 * every city, disabled on every city, with a tooltip explaining a feature
 * nobody can turn on.
 *
 * `taxi` is left out for a different reason: it is a distinction nobody can
 * act on. The engine's taxi model is the car model plus permission to use
 * ways tagged taxi-accessible — bus and taxi lanes, some restricted streets
 * — so where a city has those mapped it cuts a corner or two, and where it
 * has not it draws the same line as Car. Nothing in the form steers it:
 * there are no taxi controls, only the road preferences every driving mode
 * shares. Offering a mode whose honest description is "much the same,
 * sometimes" invites an operator to choose it and expect something of it.
 *
 * Both stay in TRAVEL_MODES above and both remain values the API accepts: a
 * saved route or a route style carrying either keeps working and keeps
 * displaying its own name. This decides what can be CHOSEN, not what exists.
 * Model what a cab actually does — ranks, pickup restrictions, driver hours
 * — and taxi belongs back in this list; build the transit graph and so does
 * transit.
 */
export const SELECTABLE_TRAVEL_MODES = TRAVEL_MODES.filter(
  (mode) => mode !== 'transit' && mode !== 'taxi',
);

/**
 * How a mode reads mid-sentence: "24 min driving", "40 min walking".
 *
 * Separate from MODE_LABELS because those name the control ("Car", "Walk")
 * and these describe the journey. The itinerary said "driving" whatever was
 * chosen, so a walking route reported itself as a drive.
 */
export const MODE_GERUNDS: Record<TravelMode, string> = {
  walk: 'walking',
  bike: 'cycling',
  motorcycle: 'riding',
  car: 'driving',
  taxi: 'by taxi',
  truck: 'driving',
  transit: 'by public transport',
};

export const MODE_LABELS: Record<TravelMode, string> = {
  walk: 'Walk',
  bike: 'Bicycle',
  motorcycle: 'Motorcycle',
  car: 'Car',
  taxi: 'Taxi',
  truck: 'Truck',
  transit: 'Public transport',
};

export interface LatLng {
  lat: number;
  lng: number;
}

export type PlaceRef =
  | { kind: 'coords'; lat: number; lng: number; label?: string }
  | { kind: 'poi'; poiId: string };

export type CityStatus = 'draft' | 'importing' | 'ready' | 'error';

export interface SafetyWeights {
  hospital: number;
  police: number;
  fire_station: number;
  max_useful_distance_m: number;
}

export interface City {
  id: string;
  slug: string;
  name: string;
  countryCode: string;
  timezone: string;
  units: 'imperial' | 'metric';
  status: CityStatus;
  statusMessage: string | null;
  center: LatLng;
  bbox: [number, number, number, number];
  defaultAvoidDistanceM: number;
  safetyWeights: SafetyWeights;
  otpSlot: 'a' | 'b' | 'c' | null;
  poiCount: number;
  gtfsFeedEndDate: string | null;
  /** True once Planetiler has generated this city's vector basemap. */
  hasBasemap: boolean;
  /** What the build pipeline has actually produced, read from disk. */
  hasExtract: boolean;
  hasRoutingTiles: boolean;
  /** Which dataset this city's suggestions are drawn from. */
  poiSource: 'osm' | 'fsq' | 'both';
  /** A real outline is stored, so imports trim to it rather than the bbox. */
  hasBoundary: boolean;
}

/** Cached provider data for one place. Absent until enrichment reaches it. */
export interface CachedPlaceData {
  externalId: string | null;
  rating: number | null;
  userRatingCount: number | null;
  priceLevel: number | null;
  primaryType: string | null;
  businessStatus: string | null;
  /** Provider's structured weekly hours; shape varies, so read defensively. */
  openingHours: unknown | null;
  matchConfidence: number | null;
  expiresAt: string;
}

/**
 * One photograph, as a URL the app can load directly.
 *
 * The provider's own reference never reaches the browser — the API redeems it
 * and redirects. `attributions` must be shown wherever the picture is.
 */
export interface PlacePhoto {
  url: string;
  widthPx: number | null;
  heightPx: number | null;
  attributions: { displayName: string; uri: string | null }[];
}

export interface PlaceDetails {
  poi: Poi;
  /**
   * The provider's record, when it was matched confidently enough to trust.
   * Null both when nobody has looked and when the answer was rejected —
   * `lookedUp` separates those.
   */
  external: CachedPlaceData | null;
  /**
   * Why there is no provider data, when there is none.
   *
   * `never` nobody has looked · `not-found` looked, nothing there ·
   * `unconfirmed` looked, and the answer was a different place ·
   * `matched` looked, believed, and present in `external`.
   */
  lookupOutcome: 'never' | 'not-found' | 'unconfirmed' | 'matched';
  /** Empty when the provider had none, or photographs are switched off. */
  photos: PlacePhoto[];
  /** Required wherever provider content is shown. Null when none is. */
  attribution: string | null;
}

export interface Category {
  id: string;
  key: string;
  label: string;
  group: string;
  icon: string | null;
  defaultDwellMinutes: number;
  isSafetyInfrastructure: boolean;
  isAvoidable: boolean;
  isVisitable: boolean;
  /** Metres to hold from these when avoided. Null uses the plan's distance. */
  avoidDistanceM?: number | null;
  poiCount?: number;
}

export interface Poi {
  id: string;
  cityId: string;
  /** Which city holds it. Present on searches; used to tell two alike apart. */
  cityName?: string;
  categoryId: string;
  categoryKey: string;
  categoryLabel: string;
  name: string;
  lat: number;
  lng: number;
  address: string | null;
  description: string | null;
  rating: number | null;
  openingHours: string | null;
  dwellMinutes: number;
  /** Which dataset this came from — drives the attribution line. */
  source: 'osm' | 'manual' | 'fsq';
  phone: string | null;
  website: string | null;
}

/**
 * A ranked candidate, offered before anything is planned.
 *
 * Carries what a list needs and no more — enough to rebuild a Poi for the
 * trip without a second request.
 */
export interface StopRecommendation {
  poiId: string;
  name: string;
  categoryKey: string;
  categoryLabel: string;
  lat: number;
  lng: number;
  address: string | null;
  dwellMinutes: number;
  /** 0-1, relative to the best candidate in this corridor. */
  score: number;
  rating: number | null;
  userRatingCount: number | null;
  /**
   * Confirmed against the place provider. Not the same as having a rating —
   * a monument can be verified and carry no reviews.
   */
  verified: boolean;
  /**
   * Minutes this adds to the journey, there and back. Routed over real roads
   * by our own engine, not guessed from straight-line distance. Null when it
   * could not be routed, and for transit.
   */
  detourMinutes: number | null;
  breakRole: 'lunch' | 'coffee' | null;
}

export type ProminenceBias = 'popular' | 'neutral' | 'hidden';

export interface RouteStyle {
  id: string;
  key: string;
  cityId: string | null;
  label: string;
  icon: string | null;
  description: string;
  categoryKeys: string[];
  boostCategoryKeys: string[];
  prominenceBias: ProminenceBias;
  defaultMode: TravelMode;
  safetyPreference: 'off' | 'prefer';
  dwellMultiplier: number;
  maxStops: number;
  timeOfDay: 'any' | 'morning' | 'afternoon' | 'evening';
}

/**
 * A style as Admin sees it. `RouteStyle` is what the planner offers — one
 * row per key, a city's override already merged in; this is every row,
 * active or not, so a city-specific style can be edited beside the global
 * one it overrides rather than in place of it.
 */
export interface AdminRouteStyle extends RouteStyle {
  isActive: boolean;
  sortOrder: number;
  /** Null for a style available in every city. */
  cityName: string | null;
}

/** A result from an external search provider. Never stored locally. */
export interface ExternalPlace {
  id: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  /** `coords` is a coordinate the traveller typed, not a provider result. */
  source: 'google' | 'coords';
}

/** A place somebody entered by hand, rather than one an import produced. */
export interface ManualPoi {
  id: string;
  cityId: string;
  categoryId: string;
  categoryKey: string;
  categoryLabel: string;
  name: string;
  lat: number;
  lng: number;
  address: string | null;
  description: string | null;
  website: string | null;
  phone: string | null;
  rating: number | null;
  dwellMinutesOverride: number | null;
  isActive: boolean;
  createdAt: string;
  /** Which import produced it, or `manual` for one entered by hand. */
  source: 'osm' | 'manual' | 'fsq';
  /** A date the dataset says it shut. Never suggested while set. */
  closedOn: string | null;
  /** 0–69, the planner's own notability measure from the place's own tags. */
  prominence: number;
}

/** The fields a person fills in to add one. */
export interface ManualPoiInput {
  cityId: string;
  categoryId: string;
  name: string;
  lat: number;
  lng: number;
  address?: string | null;
  description?: string | null;
  website?: string | null;
  phone?: string | null;
  rating?: number | null;
  dwellMinutesOverride?: number | null;
}

/**
 * A provider result offered to Admin, with what we already hold beside it.
 *
 * Nothing here is stored as it stands: a result becomes a draft to check and
 * save as our own record.
 */
export interface AdminPlaceResult {
  id: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  rating: number | null;
  ratingCount: number | null;
  website: string | null;
  phone: string | null;
  primaryType: string | null;
  businessStatus: string | null;
  /** The nearest thing already in the database, matched or not. */
  existing: DuplicateCandidate | null;
  /** Our category for the provider's kind, when it maps to one. */
  suggestedCategoryId: string | null;
}

/** An existing place a proposed entry might be a second copy of. */
export interface DuplicateCandidate {
  id: string;
  name: string;
  categoryKey: string;
  categoryLabel: string;
  source: 'osm' | 'manual' | 'fsq';
  address: string | null;
  lat: number;
  lng: number;
  distanceM: number;
  /** Token overlap with the proposed name, 0 to 1. */
  nameSimilarity: number;
  /** Near enough AND named alike enough to be treated as the same place. */
  isMatch: boolean;
}

/** What was found at a coordinate, for labelling a dropped pin. */
export interface PlaceDescription {
  name: string | null;
  address: string | null;
  source: 'google' | 'local' | 'none';
  /** How far the named place is from the pin. Local matches only. */
  distanceM?: number;
  lat: number;
  lng: number;
}

/** A spot identified only by position. */
export interface CoordStop {
  lat: number;
  lng: number;
  label?: string;
  dwellMinutes?: number;
}

export interface BreakRequest {
  /**
   * A place chosen for this break. Absent means the planner picks one near
   * where the traveller will be when it is due.
   */
  poiId?: string;
  targetMinutesFromStart: number;
  /**
   * True when no particular hour was asked for and the planner should fit the
   * break wherever the round allows. `targetMinutesFromStart` is then ignored.
   */
  anytime?: boolean;
  durationMinutes: number;
  flexibilityMinutes: number;
}

// ── trips ───────────────────────────────────────────────────────────

/**
 * One day of a trip. Every field is optional and falls back to the trip's
 * defaults, so a week based in one city is seven dates and nothing else.
 */
export interface TripDayRequest {
  /** YYYY-MM-DD. Opening hours are read against this day's weekday. */
  date?: string;
  /** Absent means "wherever yesterday finished". */
  start?: PlaceRef;
  end?: PlaceRef;
  /** A day may be walked while the next is driven. */
  mode?: TravelMode;
  cityId?: string;
  startTime?: string;
  availableMinutes?: number;
  routeStyleKeys?: string[];
  themeKey?: string;
  mustVisitPoiIds?: string[];
  /** Spots marked on the map for this day, which have no place record. */
  mustVisitPoints?: CoordStop[];
  interestCategoryKeys?: string[];
  boostCategoryKeys?: string[];
  dwellMinutesByCategoryKey?: Record<string, number>;
  /** `null` declines the trip's break for this day; omitted inherits it. */
  lunch?: BreakRequest | null;
  coffee?: BreakRequest | null;
  /** Replaces the trip's avoidance for this day, rather than adding to it. */
  avoid?: PlanRequest['avoid'];
  /** Shape-only points for this day's path. Indexed against its own legs. */
  viaPoints?: PlanRequest['viaPoints'];
  /** Stay times for this day's places, keyed as `stopOrder` is. */
  stopDwellMinutes?: Record<string, number>;
  /** The road answers for this day, replacing the trip's. */
  routingOptions?: PlanRequest['routingOptions'];
  /** What this day should be like, replacing the trip's. */
  routeCharacteristics?: PlanRequest['routeCharacteristics'];
  /** Kept in the trip so the day can be switched back on. */
  skip?: boolean;
}

export interface TripRequest {
  cityId: string;
  defaults: {
    start: PlaceRef;
    /** Absent means each day returns to its start. */
    end?: PlaceRef;
    mode: TravelMode;
    startTime?: string;
    availableMinutes?: number;
    routeStyleKeys?: string[];
    interestCategoryKeys?: string[];
    boostCategoryKeys?: string[];
    dwellMinutesByCategoryKey?: Record<string, number>;
    lunch?: BreakRequest;
    coffee?: BreakRequest;
    avoid?: PlanRequest['avoid'];
    safetyPreference?: 'off' | 'prefer';
    avoidTolls?: boolean;
    maxStops?: number;
    /** The road answers for every day of the trip. */
    routingOptions?: PlanRequest['routingOptions'];
    /** Stay times per place, for every day of the trip. */
    stopDwellMinutes?: Record<string, number>;
    /** What every day should be like: near help, scenic, quiet, 0-1 each. */
    routeCharacteristics?: PlanRequest['routeCharacteristics'];
  };
  days: TripDayRequest[];
  excludePoiIds?: string[];
}

export interface TripDay {
  index: number;
  date: string | null;
  /** Null when the day could not be planned; `error` then says why. */
  plan: RoutePlan | null;
  error: string | null;
  skipped: boolean;
  /** The request this day was planned from, defaults already resolved. */
  request: PlanRequest;
}

export interface TripPlan {
  cityId: string;
  generatedAt: string;
  days: TripDay[];
  totals: {
    days: number;
    plannedDays: number;
    stops: number;
    travelMinutes: number;
    dwellMinutes: number;
    distanceMeters: number;
  };
  warnings: string[];
}

export interface SavedTripSummary {
  /** Who saved it, from their login. '' on operations saved before that. */
  savedBy: string;
  id: string;
  cityId: string;
  cityName: string;
  name: string;
  dayCount: number;
  plannedDays: number;
  stopCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SavedTrip extends SavedTripSummary {
  request: TripRequest;
  plan: TripPlan;
}

export interface PlanRequest {
  cityId: string;
  start: PlaceRef;
  end: PlaceRef;
  mode: TravelMode;
  startTime?: string;
  /**
   * YYYY-MM-DD in the city's timezone. Opening hours are read against this
   * day's weekday; omitted means today, as it always has.
   */
  date?: string;
  availableMinutes?: number;
  routeStyleKey?: string;
  /** Several presets at once. Their categories combine; the first sets pacing. */
  routeStyleKeys?: string[];
  /**
   * A themed day: a share of the stops reserved for the theme's categories
   * BEFORE the optimiser runs, which is what a route style does not do.
   * The planner has always taken it; it reaches the request through the
   * assistant, and through the themed alternatives beside the itinerary.
   */
  themeKey?: string;
  mustVisitPoiIds: string[];
  interestCategoryKeys: string[];
  /** Ranked higher among those, without a promised share. */
  boostCategoryKeys?: string[];
  /** Stay time per kind of site, for this round. Beats the emphasis's. */
  dwellMinutesByCategoryKey?: Record<string, number>;
  /** Places never to suggest — removed from the itinerary by hand. */
  excludePoiIds?: string[];
  mustVisitPoints?: CoordStop[];
  avoid: {
    poiIds: string[];
    categoryKeys?: string[];
    points?: CoordStop[];
    distanceM?: number;
    /** Per-place and per-category radii. Each beats the one below it. */
    distanceByPoiId?: Record<string, number>;
    distanceByCategoryKey?: Record<string, number>;
  };
  safetyPreference: 'off' | 'prefer';
  lunch?: BreakRequest;
  coffee?: BreakRequest;
  avoidTolls?: boolean;
  maxStops?: number;
  /** Visit the stops in exactly the order given, rather than optimising it. */
  stopOrderLocked?: boolean;
  /**
   * The road questions answered for this plan rather than by an emphasis.
   *
   * Same three groups an emphasis carries, and the same meanings. Sent only
   * where somebody actually answered one: an empty object would say "no
   * preferences" where the truth is that nobody was asked, and the
   * emphasis's own answers would have nothing to merge with.
   */
  routingOptions?: {
    use_highways?: number;
    use_tolls?: number;
    use_ferry?: number;
    use_living_streets?: number;
    shortest?: boolean;
    /**
     * Motorway, trunk and primary together. Not a router option — applied by
     * choosing between the ways round it offered, because its costing has no
     * lever for road classes below motorway.
     */
    use_main_roads?: number;
    /** Ordinary streets people live on — highway=residential. */
    use_residential?: number;
  };
  routeCharacteristics?: { safety?: number; scenic?: number; quiet?: number };
  pathOptions?: {
    roadRadiusM?: number;
    extraPct?: number;
    extraMinutes?: number;
    differentPathBufferM?: number;
    differentPathKeepClearM?: number;
  };
  /**
   * Lines this route may not reuse — normally the plan already on screen.
   *
   * The middle of each is excluded, so the same stops under a different
   * emphasis have to be reached by another road rather than coming back as
   * the identical line with a different label on it. The ends stay open, and
   * so does a circle around every stop.
   */
  differentFrom?: [number, number][][];
  /**
   * The visiting order, as candidate ids — a place id, or `pin-N` for the
   * Nth entry of mustVisitPoints. Read only when stopOrderLocked is set.
   */
  stopOrder?: string[];
  /**
   * Points to route through without stopping, to shape the path. Not stops:
   * no dwell time and no place in the itinerary.
   */
  viaPoints?: { lat: number; lng: number; afterStopIndex: number }[];
  /**
   * How long to stay at particular stops, overriding the default for each.
   * Keyed as stopOrder is — a place id, or `pin-N`.
   */
  stopDwellMinutes?: Record<string, number>;
  /**
   * How to travel out of particular stops, overriding the day's own mode.
   *
   * Keyed by the stop each leg LEAVES: `start`, then a place id, then `at:N`
   * for a stop with no id. Selection and ordering still happen in `mode` — a
   * matrix has to exist before an order does — so this changes how the day is
   * travelled rather than which places it visits.
   */
  legModes?: Record<string, TravelMode>;
}

/** Another way round for the same start and end. Direct routes only. */
export interface RouteAlternative {
  index: number;
  distanceMeters: number;
  durationMinutes: number;
  geometry: [number, number][];
  /**
   * How this way round answers the road that was asked for, against the one
   * drawn. Empty unless a road control was answered.
   */
  roadAnswer: string;
}

export type StopKind = 'start' | 'end' | 'visit' | 'lunch' | 'coffee';

export interface PlanStop {
  kind: StopKind;
  poiId: string | null;
  name: string;
  lat: number;
  lng: number;
  categoryKey: string | null;
  arrivalMinutes: number;
  departureMinutes: number;
  dwellMinutes: number;
  arrivalTime: string;
  departureTime: string;
  note: string | null;
  outsideOpeningHours: boolean | null;
}

export interface TransitLegDetail {
  mode: string;
  routeName: string | null;
  headsign: string | null;
  fromStop: string | null;
  toStop: string | null;
  durationMinutes: number;
}

export interface PlanLeg {
  fromIndex: number;
  toIndex: number;
  mode: TravelMode;
  distanceMeters: number;
  durationMinutes: number;
  /** [lng, lat] pairs, ready for GeoJSON. */
  geometry: [number, number][];
  transitDetail?: TransitLegDetail[];
}

/**
 * One turn of a leg, in the router’s own words.
 *
 * Not part of a plan. Asked for separately, once, by whoever wants a file:
 * see `api.directions`. A plan is re-sent on every edit and kept in every
 * saved route, and prose nobody reads has no business riding along.
 */
export interface Maneuver {
  /** The sentence a driver is given, e.g. "Turn right onto Main Street". */
  instruction: string;
  streetNames: string[];
  distanceMeters: number;
  durationSeconds: number;
  /** Valhalla’s own maneuver type, for telling a destination from a turn. */
  type: number;
  /** Where the turn happens, off the leg’s own line. */
  lat: number;
  lng: number;
}

/** One leg’s worth of turn-by-turn. No geometry: the caller has the line. */
export interface DirectionsLeg {
  fromIndex: number;
  toIndex: number;
  distanceMeters: number;
  durationSeconds: number;
  maneuvers: Maneuver[];
}

/**
 * One time a place has been covered before.
 *
 * "Covered" means a saved round or a saved operation stops there. Nobody
 * confirms a visit, so this is work somebody committed to rather than a fact
 * about the ground, and the panel that shows it says so.
 */
export interface Covering {
  /** The saved route or operation it came from. */
  id: string;
  name: string;
  source: 'route' | 'operation';
  /** Who saved it. Empty on records older than attribution. */
  by: string;
  /**
   * What to call them, where that account has been given a name.
   *
   * Empty otherwise, and every reader falls back to `by`. Beside the
   * username rather than instead of it, because `by` is what identifies the
   * person — two people may yet be called the same thing.
   */
  byName: string;
  /** When it was last saved, ISO. */
  at: string | null;
  /**
   * Minutes the plan ALLOWED there — not minutes anybody spent.
   *
   * Zero where the plan set no time, which a start or an end usually does
   * not. The panel shows that as "no time set" rather than as nothing.
   */
  dwellMinutes: number;
  /** Which day of an operation, 1-based. Zero for a single round. */
  day: number;
  /** How many stops that route, or that day of that operation, has in all. */
  planStops: number;
  /** The date it was planned for, YYYY-MM-DD; '' where none was set. */
  planDate: string;
  /**
   * Where the stop sits in that plan's day, 1-based, and what the plan does
   * either side of it.
   *
   * Empty neighbours mean the day starts or ends there. With `planStops`
   * these answer HOW a place is covered rather than merely whether it is —
   * first call of the morning, or wedged between two others at the far end
   * of an afternoon.
   */
  stopIndex: number;
  prevStop: string;
  nextStop: string;
  /** The hour that stop is due, HH:MM; '' where the plan sets none. */
  planTime: string;
  /**
   * How far that saved stop is from the point asked about, in metres.
   *
   * Zero when the match was by place id, which is exact. A dropped pin has
   * no id, so it is matched on the ground instead, and the screen shows the
   * distance rather than asserting a sameness nobody can check.
   */
  metres: number;
  /** What the saved plan calls the stop that matched. */
  stopName: string;
}

/** One asked-about stop's whole history, most recent first. */
export interface PoiCoverage {
  /** Empty for a point: a dropped pin has no place to be. */
  poiId: string;
  /** What the caller asked with — a place id, or a point's own key. */
  key: string;
  coverings: Covering[];
  totalMinutes: number;
}

/**
 * How often one place has been planned, with none of the detail.
 *
 * `PoiCoverage` answers "what exactly has happened here" for a handful of
 * places and costs a row per covering to do it. Somebody picking places off
 * a catalogue is asking a smaller question of a far larger set — has this
 * one been worked at all, and how heavily — which is this.
 */
export interface PlaceTally {
  poiId: string;
  /**
   * Saved plan-days stopping here.
   *
   * Counted as the coverage window counts: a day of an operation is its own,
   * and a round trip through the same place twice is one. The two screens
   * therefore never disagree about the same place.
   */
  plans: number;
  /** The latest date any of them is for, YYYY-MM-DD; '' where none sets one. */
  lastPlanDate: string;
  /**
   * Where the place is, and what it is called.
   *
   * Carried with the figure because the map marks every planned place in
   * the city at once, while the list beside it has read only a page or
   * two. From the catalogue where the place is still in it, and from the
   * saved stop where it is not — a place dropped from the catalogue has
   * still been planned. Both zero where neither knew, which is a place
   * that cannot be drawn.
   */
  name: string;
  lat: number;
  lng: number;
  categoryLabel: string;
}

/** A stop with no place id, asked about by where it is. */
export interface CoveragePoint {
  key: string;
  lat: number;
  lng: number;
}

export interface AvoidedZone {
  poiId: string;
  /** Set when the zone came from an avoided category rather than a place. */
  fromCategoryKey?: string;
  name: string;
  categoryKey: string;
  lat: number;
  lng: number;
  radiusM: number;
  polygon: [number, number][];
  /** Only possible on transit, which cannot honour polygon exclusions. */
  violated: boolean;
}

export interface SafetyAssessment {
  score: number;
  label: string;
  disclaimer: string;
  sampleCount: number;
  breakdown: {
    categoryKey: string;
    weight: number;
    averageDistanceM: number;
    nearestDistanceM: number;
  }[];
}

/** One category's contribution, in the numbers a score must be traceable to. */
export interface IpsCategoryBreakdown {
  categoryKey: string;
  label: string;
  weight: number;
  /** Renormalised across enabled categories; these sum to 1. */
  effectiveWeight: number;
  /** r(c) — inside this, a waypoint scores a full mark. */
  radiusM: number;
  facilitiesWithinRadius: number;
  worstWaypointDistanceM: number | null;
  /** C(c) — the mean across every waypoint. */
  categoryScore: number;
}

export interface IpsAssessment {
  /** 0–100. */
  score: number;
  label: string;
  disclaimer: string;
  /** Waypoints measured. The path between them is not sampled. */
  waypointCount: number;
  breakdown: IpsCategoryBreakdown[];
}

/** A theme the traveller can ask a day to be built around. */
/** A named set of categories, ordered, and whether it is infrastructure. */
export interface CategoryGroup {
  key: string;
  label: string;
  /** Ground a route is planned AROUND rather than towards. */
  isInfrastructure: boolean;
  sortOrder: number;
}

export interface RouteTheme {
  id: string;
  key: string;
  label: string;
  description: string;
  categoryKeys: string[];
  /** Categories this emphasis keeps the route clear of, added to the request's. */
  avoidCategoryKeys: string[];
  /** Ranked higher when the shortlist is scored, without a promised share. */
  boostCategoryKeys: string[];
  /** How far to hold from what it excludes. Null falls through to the category. */
  avoidDistanceM: number | null;
  /** How far to hold from a particular excluded category, where they differ. */
  avoidDistanceByCategory: Record<string, number>;
  /** Minutes at a stop, per category. Anything unnamed keeps the category's own. */
  stopDwellMinutes: Record<string, number>;
  /** What kind of road it prefers, in the router's own terms. */
  routingOptions: {
    use_highways?: number;
    use_tolls?: number;
    use_ferry?: number;
    use_living_streets?: number;
    shortest?: boolean;
    use_main_roads?: number;
    use_residential?: number;
  };
  /**
   * What the journey should be like, 0-1 each. Measured against our own
   * places, not the router's - it has no idea what "scenic" means.
   */
  routeCharacteristics: {
    safety?: number;
    scenic?: number;
    quiet?: number;
  };
  /** The numbers that weigh the two above. Absent means the global setting. */
  pathOptions: {
    roadRadiusM?: number;
    extraPct?: number;
    extraMinutes?: number;
    differentPathBufferM?: number;
    differentPathKeepClearM?: number;
  };
  /** The promise: this fraction of non-anchor stops comes from those. */
  minShare: number;
  isActive: boolean;
  sortOrder: number;
}

/** What a theme actually delivered, as opposed to what it promised. */
export interface ThemeOutcome {
  key: string;
  label: string;
  minShare: number;
  nonAnchorStops: number;
  onThemeStops: number;
  share: number;
  met: boolean;
}

export interface RoutePlan {
  cityId: string;
  mode: TravelMode;
  styleKey: string | null;
  /** Every style applied, in the order given. */
  styleKeys?: string[];
  /** What a theme delivered, when one was asked for. Null otherwise. */
  theme?: ThemeOutcome | null;
  generatedAt: string;
  startTime: string;
  stops: PlanStop[];
  legs: PlanLeg[];
  totals: {
    travelMinutes: number;
    dwellMinutes: number;
    totalMinutes: number;
    distanceMeters: number;
  };
  /**
   * Proximity of the stops to published facilities.
   *
   * Never called "safety" in the interface — it measures presence of
   * infrastructure, not risk. Replaces the old route-sampled measure, so a
   * plan saved before this exists carries `safety` instead and shows nothing.
   */
  infrastructureProximity: IpsAssessment | null;
  avoidedZones: AvoidedZone[];
  dropped: { poiId: string; name: string; reason: string }[];
  /** Present when a larger time budget would recover dropped places. */
  suggestion: { availableMinutes: number; recovers: number } | null;
  /** Other ways round. Only produced for a direct route, so often empty. */
  alternatives: RouteAlternative[];
  warnings: string[];
  /**
   * What the drawn route actually runs on, for the road controls that
   * were answered — including "no toll road is used on this route",
   * which is the answer when a control correctly did nothing.
   */
  roadNotes: string[];
  /** What the DRAWN line runs on, for the road that was asked for. */
  roadAnswer: string;
}

// ── saved routes ────────────────────────────────────────────────────
export interface SavedRouteSummary {
  id: string;
  cityId: string;
  cityName: string;
  name: string;
  mode: string;
  stopCount: number;
  /** Who saved it, by username. Empty for routes saved before this existed. */
  savedBy: string;
  totalMinutes: number;
  createdAt: string;
  updatedAt: string;
}

export interface SavedRoute extends SavedRouteSummary {
  request: PlanRequest;
  plan: RoutePlan;
  notes: Record<string, string>;
}

// ── admin ───────────────────────────────────────────────────────────
export type JobType =
  | 'osm_download'
  | 'poi_import'
  | 'valhalla_build'
  | 'otp_build'
  | 'basemap_build'
  | 'verify';

export type JobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'interrupted';

export interface Job {
  id: string;
  cityId: string | null;
  type: JobType;
  status: JobStatus;
  progress: number;
  message: string | null;
  log: string;
  params: Record<string, unknown>;
  result: unknown;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface PoiImportPreview {
  preview: boolean;
  total: number;
  breakdown: { categoryKey: string; count: number }[];
}

export interface VerifyResult {
  passed: boolean;
  checks: { name: string; ok: boolean; detail: string; required: boolean }[];
}

export interface HealthReport {
  ok: boolean;
  engines: {
    database: { ok: boolean; detail: string };
    routing: { ok: boolean; detail: string };
    solver: { ok: boolean; detail: string };
    transit: { slot: string; ok: boolean; detail: string }[];
  };
  cities: { total: number; ready: number };
}

export interface SystemStatus {
  /** Real app health: backend reachable + database connection live. */
  backend: { ok: boolean; detail: string };
  disk: {
    freeBytes: number;
    totalBytes: number;
    free: string;
    total: string;
    lowSpace: boolean;
  } | null;
  queue: { active: string | null; queued: number };
  dataDir: string;
  /** Reported separately so admin can tell "no key" from "switched off". */
  externalSearch?: { keyConfigured: boolean; enabled: boolean };
  /**
   * Job types this deployment will refuse, keyed by type, with the reason.
   *
   * A deployment that serves prebuilt city data has no container runtime, so
   * the basemap build cannot run there — and it knows that at boot. Offering
   * the button anyway produces a red failure that reads as a broken
   * deployment rather than a job that belongs somewhere else.
   */
  unavailableJobs: Record<string, string>;
}

/** Shape of every error response the API produces. */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

/** A boundary found by name, for setting up a city. */
export interface GeocodedPlace {
  displayName: string;
  /** city, county, state - what actually matched. */
  kind: string;
  /** [minLng, minLat, maxLng, maxLat] - already in this app's order. */
  bbox: [number, number, number, number];
  lat: number;
  lng: number;
}
